import { combineEpics, Epic, ofType } from 'redux-observable';
import { of } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import {
  chooseKenticoProject,
  forgotPassword,
  forgotPasswordError,
  forgotPasswordSuccess,
  getProjectPrefix,
  getProjectPrefixError,
  getProjectPrefixSuccess,
  login,
  loginError,
  loginSuccess,
  resetPassword,
  resetPasswordError,
  resetPasswordSuccess,
  verifyResetPasswordToken,
  verifyResetPasswordTokenError,
  verifyResetPasswordTokenSuccess,
} from 'store/auth/auth.actions';
import {
  addToQueue,
  addToQueueError,
  addToQueueSuccess,
  getContent,
  getContentError,
  getContentSuccess,
  sendForTranslation,
  sendForTranslationError,
  sendForTranslationSuccess,
  setPagination,
} from 'store/content/content.actions';
import {
  changeDueDateJob,
  changeDueDateJobError,
  changeDueDateJobSuccess,
  contentChangedError,
  contentChangedIgnore,
  contentChangedSend,
  contentChangedSuccess,
  contentChangedUpdate,
  contentRemovedIgnore,
  contentRemovedIgnoreError,
  contentRemovedIgnoreSuccess,
  contentRemovedRemove,
  contentRemovedRemoveError,
  contentRemovedRemoveSuccess,
  errorMessageIgnore,
  errorMessageIgnoreError,
  errorMessageIgnoreSuccess,
  exportListToFile,
  exportListToFileError,
  exportListToFileSuccess,
  getProjectChangedContent,
  getProjectChangedContentError,
  getProjectChangedContentSuccess,
  getProjects,
  getProjectsError,
  getProjectsSuccess,
  modifyLanguages,
  modifyLanguagesError,
  modifyLanguagesSuccess,
} from 'store/projects/projects.actions';
import {
  getActiveProjects,
  getActiveProjectsError,
  getActiveProjectsSuccess,
  getAvailableLanguages,
  getAvailableLanguagesError,
  getAvailableLanguagesSuccess,
  getCustomers,
  getCustomersError,
  getCustomersSuccess,
  getTemplatesByCustomerId,
  getTemplatesByCustomerIdError,
  getTemplatesByCustomerIdSuccess,
} from 'store/settings/settings.actions';
import {
  getSimilarOperationIds,
  getSimilarOperationIdsError,
  getSimilarOperationIdsSuccess,
  performOperation,
  performOperationError,
  performOperationSuccess,
} from '../jobs/jobs.actions';
import {
  setAuthSpinner,
  setContentSpinner,
  setFileSpinner,
  setProjectsSpinner,
  setTranslateSpinner,
  setXtmActiveProjectsSpinner,
  setXtmCustomerSpinner,
  setXtmLanguagesSpinner,
  setXtmTemplateSpinner,
  toggleJobActionSpinner,
  toggleSecondJobActionSpinner,
  toggleThirdJobActionSpinner,
} from './spinner.actions';

const authSpinnerEpic: Epic = (action$) =>
  action$.pipe(
    ofType(
      login.type,
      loginError.type,
      loginSuccess.type,
      resetPassword.type,
      resetPasswordError.type,
      resetPasswordSuccess.type,
      verifyResetPasswordToken.type,
      verifyResetPasswordTokenError.type,
      verifyResetPasswordTokenSuccess.type,
      chooseKenticoProject.type,
      forgotPassword.type,
      forgotPasswordError.type,
      forgotPasswordSuccess.type,
      getProjectPrefix.type,
      getProjectPrefixSuccess.type,
      getProjectPrefixError.type,
      getProjectChangedContent.type,
      getProjectChangedContentError.type,
      getProjectChangedContentSuccess.type,
    ),
    switchMap(({ type }) => {
      if ((type as string).endsWith('SUCCESS') || (type as string).endsWith('ERROR')) {
        return of(setAuthSpinner(false));
      }
      return of(setAuthSpinner(true));
    }),
  );

const xtmCustomerSpinnerEpic: Epic = (action$) =>
  action$.pipe(
    ofType(getCustomers.type, getCustomersSuccess.type, getCustomersError.type),
    switchMap(({ type }) => {
      if ((type as string).endsWith('SUCCESS') || (type as string).endsWith('ERROR')) {
        return of(setXtmCustomerSpinner(false));
      }
      return of(setXtmCustomerSpinner(true));
    }),
  );

const xtmTemplateSpinnerEpic: Epic = (action$) =>
  action$.pipe(
    ofType(getTemplatesByCustomerId.type, getTemplatesByCustomerIdSuccess.type, getTemplatesByCustomerIdError.type),
    switchMap(({ type }) => {
      if ((type as string).endsWith('SUCCESS') || (type as string).endsWith('ERROR')) {
        return of(setXtmTemplateSpinner(false));
      }
      return of(setXtmTemplateSpinner(true));
    }),
  );

const xtmActiveProjectsSpinnerEpic: Epic = (action$) =>
  action$.pipe(
    ofType(getActiveProjects.type, getActiveProjectsSuccess.type, getActiveProjectsError.type),
    switchMap(({ type }) => {
      if ((type as string).endsWith('SUCCESS') || (type as string).endsWith('ERROR')) {
        return of(setXtmActiveProjectsSpinner(false));
      }
      return of(setXtmActiveProjectsSpinner(true));
    }),
  );

const xtmLanguagesSpinnerEpic: Epic = (action$) =>
  action$.pipe(
    ofType(getAvailableLanguages.type, getAvailableLanguagesSuccess.type, getAvailableLanguagesError.type),
    switchMap(({ type }) => {
      if ((type as string).endsWith('SUCCESS') || (type as string).endsWith('ERROR')) {
        return of(setXtmLanguagesSpinner(false));
      }
      return of(setXtmLanguagesSpinner(true));
    }),
  );

const contentSpinnerEpic: Epic = (action$) =>
  action$.pipe(
    ofType(getContent.type, getContentSuccess.type, getContentError.type, setPagination.type),
    switchMap(({ type }) => {
      if ((type as string).endsWith('SUCCESS') || (type as string).endsWith('ERROR')) {
        return of(setContentSpinner(false));
      }
      return of(setContentSpinner(true));
    }),
  );

const projectsSpinnerEpic: Epic = (action$) =>
  action$.pipe(
    ofType(getProjects.type, getProjectsSuccess.type, getProjectsError.type),
    switchMap(({ type }) => {
      if ((type as string).endsWith('SUCCESS') || (type as string).endsWith('ERROR')) {
        return of(setProjectsSpinner(false));
      }
      return of(setProjectsSpinner(true));
    }),
  );

const fileSpinnerEpic: Epic = (action$) =>
  action$.pipe(
    ofType(exportListToFile.type, exportListToFileError.type, exportListToFileSuccess.type),
    switchMap(({ type }) => {
      if ((type as string).endsWith('SUCCESS') || (type as string).endsWith('ERROR')) {
        return of(setFileSpinner(false));
      }
      return of(setFileSpinner(true));
    }),
  );

const jobActionSpinnerEpic: Epic = (action$) =>
  action$.pipe(
    ofType(
      getSimilarOperationIds.type,
      getSimilarOperationIdsError.type,
      performOperation.type,
      performOperationSuccess.type,
      performOperationError.type,
      changeDueDateJob.type,
      changeDueDateJobError.type,
      changeDueDateJobSuccess.type,
      modifyLanguages.type,
      modifyLanguagesError.type,
      modifyLanguagesSuccess.type,
      contentChangedUpdate.type,
      contentChangedError.type,
      contentChangedSuccess.type,
      contentRemovedRemove.type,
      contentRemovedRemoveError.type,
      contentRemovedRemoveSuccess.type,
    ),
    debounceTime(100),
    switchMap(({ type }) => {
      if ((type as string).endsWith('SUCCESS') || (type as string).endsWith('ERROR')) {
        return of(toggleJobActionSpinner(false));
      }
      return of(toggleJobActionSpinner(true));
    }),
  );

const secondJobActionSpinnerEpic: Epic = (action$) =>
  action$.pipe(
    ofType(
      contentChangedSend.type,
      contentChangedError.type,
      contentChangedSuccess.type,
      contentRemovedIgnore.type,
      contentRemovedIgnoreError.type,
      contentRemovedIgnoreSuccess.type,
      getSimilarOperationIds.type,
      getSimilarOperationIdsSuccess.type,
      getSimilarOperationIdsError.type,
      performOperation.type,
      performOperationSuccess.type,
      performOperationError.type,
    ),
    debounceTime(100),
    switchMap(({ type }) => {
      if ((type as string).endsWith('SUCCESS') || (type as string).endsWith('ERROR')) {
        return of(toggleSecondJobActionSpinner(false));
      }
      return of(toggleSecondJobActionSpinner(true));
    }),
  );

const thirdJobActionSpinnerEpic: Epic = (action$) =>
  action$.pipe(
    ofType(
      contentChangedIgnore.type,
      contentChangedError.type,
      contentChangedSuccess.type,
      errorMessageIgnore.type,
      errorMessageIgnoreError.type,
      errorMessageIgnoreSuccess.type,
    ),
    debounceTime(100),
    switchMap(({ type }) => {
      if ((type as string).endsWith('SUCCESS') || (type as string).endsWith('ERROR')) {
        return of(toggleThirdJobActionSpinner(false));
      }
      return of(toggleThirdJobActionSpinner(true));
    }),
  );

const translateSpinnerEpic: Epic = (action$) =>
  action$.pipe(
    ofType(
      addToQueue.type,
      addToQueueSuccess.type,
      addToQueueError.type,
      sendForTranslation.type,
      sendForTranslationSuccess.type,
      sendForTranslationError.type,
    ),
    switchMap(({ type }) => {
      if ((type as string).endsWith('SUCCESS') || (type as string).endsWith('ERROR')) {
        return of(setTranslateSpinner(false));
      }
      return of(setTranslateSpinner(true));
    }),
  );

export const spinnerEpics = combineEpics(
  authSpinnerEpic,
  xtmCustomerSpinnerEpic,
  xtmTemplateSpinnerEpic,
  xtmActiveProjectsSpinnerEpic,
  contentSpinnerEpic,
  jobActionSpinnerEpic,
  secondJobActionSpinnerEpic,
  thirdJobActionSpinnerEpic,
  translateSpinnerEpic,
  projectsSpinnerEpic,
  fileSpinnerEpic,
  xtmLanguagesSpinnerEpic,
);
